import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';

import type {
  BuyerProposalOption,
  ConfirmOutboundSalesResultInput,
  OutboundDraftInput,
  OutboundDto,
  OutboundListFilters,
  OutboundListMeta,
  UpdateOutboundDraftInput,
} from './types';

export const fetchBuyerProposals = async (batchId: string): Promise<BuyerProposalOption[]> => {
  const res = await apiClient.get<ApiSuccess<BuyerProposalOption[]>>(
    `/monthly-batches/${batchId}/buyer-proposals`,
  );
  return res.data.data ?? [];
};

export const fetchOutbounds = async (
  batchId: string,
  filters: OutboundListFilters = {},
): Promise<{ data: OutboundDto[]; meta: OutboundListMeta }> => {
  const res = await apiClient.get<ApiSuccess<OutboundDto[], OutboundListMeta>>(
    `/monthly-batches/${batchId}/outbounds`,
    { params: filters },
  );
  const data = res.data.data ?? [];
  const meta = res.data.meta ?? {
    total: data.length,
    page: filters.page ?? 1,
    per_page: filters.per_page ?? data.length,
  };
  return { data, meta };
};

export const fetchOutbound = async (id: string): Promise<OutboundDto> => {
  const res = await apiClient.get<ApiSuccess<OutboundDto>>(`/outbounds/${id}`);
  return res.data.data;
};

export const createOutbound = async (
  batchId: string,
  body: OutboundDraftInput,
): Promise<OutboundDto> => {
  const res = await apiClient.post<ApiSuccess<OutboundDto>>(
    `/monthly-batches/${batchId}/outbounds`,
    body,
  );
  return res.data.data;
};

export const updateOutbound = async (
  id: string,
  body: UpdateOutboundDraftInput,
): Promise<OutboundDto> => {
  const res = await apiClient.patch<ApiSuccess<OutboundDto>>(`/outbounds/${id}`, body);
  return res.data.data;
};

export const voidOutbound = async (id: string, reason: string): Promise<OutboundDto> => {
  const res = await apiClient.post<ApiSuccess<OutboundDto>>(`/outbounds/${id}/void`, { reason });
  return res.data.data;
};

export const dispatchOutbound = async (
  id: string,
  shippingPhotoFileId: string,
): Promise<OutboundDto> => {
  const res = await apiClient.post<ApiSuccess<OutboundDto>>(`/outbounds/${id}/dispatch`, {
    shipping_photo_file_id: shippingPhotoFileId,
  });
  return res.data.data;
};

export const attachReceiptEvidence = async (
  id: string,
  receiptEvidenceFileId: string,
): Promise<OutboundDto> => {
  const res = await apiClient.post<ApiSuccess<OutboundDto>>(`/outbounds/${id}/receipt-evidence`, {
    receipt_evidence_file_id: receiptEvidenceFileId,
  });
  return res.data.data;
};

export const attachResourceChainEvidence = async (
  id: string,
  resourceChainEvidenceFileId: string,
): Promise<OutboundDto> => {
  const res = await apiClient.post<ApiSuccess<OutboundDto>>(
    `/outbounds/${id}/resource-chain-evidence`,
    { resource_chain_evidence_file_id: resourceChainEvidenceFileId },
  );
  return res.data.data;
};

const postTransition = async (
  id: string,
  action: string,
  body?: Record<string, unknown>,
): Promise<OutboundDto> => {
  const url = `/outbounds/${id}/${action}`;
  const res =
    body === undefined
      ? await apiClient.post<ApiSuccess<OutboundDto>>(url)
      : await apiClient.post<ApiSuccess<OutboundDto>>(url, body);
  return res.data.data;
};

export const confirmOutboundReceipt = (id: string) => postTransition(id, 'confirm-receipt');
export const waiveOutboundReceipt = (id: string, reason: string) =>
  postTransition(id, 'waive-receipt', { reason });
export const confirmOutboundResourceChain = (id: string) =>
  postTransition(id, 'confirm-resource-chain');
export const waiveOutboundResourceChain = (id: string, reason: string) =>
  postTransition(id, 'waive-resource-chain', { reason });
export const confirmOutboundSalesResult = async (
  id: string,
  body: ConfirmOutboundSalesResultInput,
): Promise<OutboundDto> => {
  const res = await apiClient.post<ApiSuccess<OutboundDto>>(
    `/outbounds/${id}/sales-result-confirm`,
    body,
  );
  return res.data.data;
};
export const lockOutbound = (id: string) => postTransition(id, 'lock');
